// src/pages/AnexoPreviewModal.js

import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  FaTimes, FaDownload, FaExternalLinkAlt, FaFilePdf, FaFileWord, FaFileImage,
  FaVideo, FaPaperclip, FaSpinner, FaFileExcel, FaFilePowerpoint, FaFileAlt,
  FaExclamationTriangle,
} from 'react-icons/fa';
import api from '../services/api';

// Vídeos/imagens acima desse tamanho caem no player embed do Drive.
// Ajuste à vontade — pra vídeos de treinamento de até ~50MB, o blob é instantâneo.
const BLOB_MAX_BYTES = 50 * 1024 * 1024; // 50MB

const formatBytes = (bytes = 0) => {
  const n = Number(bytes) || 0;
  if (!n) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
};

const getTipo = (mime = '', nome = '') => {
  const m = (mime || '').toLowerCase();
  const n = (nome || '').toLowerCase();
  if (m.startsWith('video/')) return 'video';
  if (m.startsWith('audio/')) return 'audio';
  if (m.startsWith('image/')) return 'imagem';
  if (m.includes('pdf') || n.endsWith('.pdf')) return 'pdf';
  if (
    m.includes('word') ||
    m.includes('officedocument.wordprocessingml') ||
    n.endsWith('.doc') || n.endsWith('.docx')
  ) return 'word';
  if (
    m.includes('sheet') || m.includes('excel') ||
    n.endsWith('.xls') || n.endsWith('.xlsx') || n.endsWith('.csv')
  ) return 'excel';
  if (
    m.includes('presentation') ||
    n.endsWith('.ppt') || n.endsWith('.pptx')
  ) return 'ppt';
  if (m.startsWith('text/') || n.endsWith('.txt')) return 'texto';
  return 'arquivo';
};

const tipoIcon = (tipo) => {
  switch (tipo) {
    case 'video': return <FaVideo />;
    case 'imagem': return <FaFileImage />;
    case 'pdf': return <FaFilePdf />;
    case 'word': return <FaFileWord />;
    case 'excel': return <FaFileExcel />;
    case 'ppt': return <FaFilePowerpoint />;
    case 'texto': return <FaFileAlt />;
    default: return <FaPaperclip />;
  }
};

export default function AnexoPreviewModal({ anexo, onClose, onDownload }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [useEmbedFallback, setUseEmbedFallback] = useState(false);

  const abortRef = useRef(null);
  const blobUrlRef = useRef(null);

  const tipo = useMemo(() => getTipo(anexo?.mimetype, anexo?.nome_original), [anexo]);
  const tamanho = Number(anexo?.tamanho_bytes) || 0;

  // Usamos blob para vídeo/imagem/áudio (autenticação garantida via axios/Bearer)
  const precisaBlob = tipo === 'video' || tipo === 'imagem' || tipo === 'audio';
  const tooLargeForBlob = tamanho > 0 && tamanho > BLOB_MAX_BYTES;

  // URLs de embed do Drive (funcionam sem o nosso auth — usam sessão Google)
  const embedUrl = useMemo(() => {
    if (anexo?.preview_embed_url) return anexo.preview_embed_url;
    if (anexo?.drive_file_id) {
      return `https://drive.google.com/file/d/${anexo.drive_file_id}/preview`;
    }
    return null;
  }, [anexo]);

  const externalUrl = anexo?.drive_web_view_link || anexo?.preview_url || embedUrl;

  // ----------------------------------------------------------------
  // Bloqueia scroll do body quando o modal estiver aberto
  // ----------------------------------------------------------------
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleEsc = (e) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', handleEsc);

    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', handleEsc);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----------------------------------------------------------------
  // Cleanup do blob e abort do axios
  // ----------------------------------------------------------------
  useEffect(() => {
    return () => {
      if (abortRef.current) {
        try { abortRef.current.abort(); } catch (_) {}
      }
      if (blobUrlRef.current) {
        try { URL.revokeObjectURL(blobUrlRef.current); } catch (_) {}
        blobUrlRef.current = null;
      }
    };
  }, []);

  // ----------------------------------------------------------------
  // Dispara o carregamento do blob quando o tipo exige
  // ----------------------------------------------------------------
  useEffect(() => {
    if (!anexo) return;

    // Se for muito grande OU o usuário escolheu fallback, usa embed do Drive
    if (precisaBlob && !tooLargeForBlob && !useEmbedFallback) {
      loadAsBlob();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anexo?.id, useEmbedFallback]);

  const loadAsBlob = async () => {
    setLoading(true);
    setError(null);
    setProgress(0);

    // Revoga blob anterior, se houver
    if (blobUrlRef.current) {
      try { URL.revokeObjectURL(blobUrlRef.current); } catch (_) {}
      blobUrlRef.current = null;
      setBlobUrl(null);
    }

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const res = await api.get(`/api/anexos/${anexo.id}/view`, {
        responseType: 'blob',
        signal: controller.signal,
        onDownloadProgress: (e) => {
          if (e.total) {
            setProgress(Math.round((e.loaded / e.total) * 100));
          } else if (tamanho) {
            setProgress(Math.round((e.loaded / tamanho) * 100));
          }
        },
      });

      // Garante que a Blob tem o mimetype correto — evita browsers "travando" o vídeo
      const expectedType = anexo.mimetype || res.data?.type || 'application/octet-stream';
      const finalBlob = res.data?.type ? res.data : new Blob([res.data], { type: expectedType });

      const url = URL.createObjectURL(finalBlob);
      blobUrlRef.current = url;
      setBlobUrl(url);
      setLoading(false);
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') {
        return;
      }
      console.error('Erro ao baixar anexo para preview:', err);
      setError(
        tipo === 'video'
          ? 'Não foi possível carregar o vídeo pelo servidor. Usando player do Google Drive.'
          : 'Não foi possível carregar o arquivo. Tente abrir no Drive ou baixar.'
      );
      setUseEmbedFallback(true);
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (abortRef.current) {
      try { abortRef.current.abort(); } catch (_) {}
    }
    if (blobUrlRef.current) {
      try { URL.revokeObjectURL(blobUrlRef.current); } catch (_) {}
      blobUrlRef.current = null;
    }
    onClose && onClose();
  };

  // ----------------------------------------------------------------
  // RENDER
  // ----------------------------------------------------------------
  const renderLoading = (label) => (
    <div className="preview-fallback">
      <div className="preview-fallback-icon preview-spin">
        <FaSpinner />
      </div>
      <h4>{label}</h4>
      <p>Baixando com segurança via servidor autenticado.</p>
      <div className="preview-progress-bar">
        <div className="preview-progress-fill" style={{ width: `${progress}%` }} />
      </div>
      <small>{progress}%{tamanho ? ` • ${formatBytes(tamanho)}` : ''}</small>
    </div>
  );

  const renderFallbackCard = (titulo, descricao, IconOverride = null) => (
    <div className="preview-fallback">
      <div className="preview-fallback-icon">
        {IconOverride || tipoIcon(tipo)}
      </div>
      <h4>{titulo}</h4>
      <p>{descricao}</p>
      <div className="preview-fallback-actions">
        {externalUrl && (
          <a
            href={externalUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="preview-action-btn primary"
          >
            <FaExternalLinkAlt /> Abrir no Drive
          </a>
        )}
        <button
          className="preview-action-btn"
          onClick={() => onDownload && onDownload(anexo.id, anexo.nome_original)}
        >
          <FaDownload /> Baixar
        </button>
      </div>
    </div>
  );

  const renderContent = () => {
    if (!anexo) return null;
    const forceEmbed = useEmbedFallback || (precisaBlob && tooLargeForBlob);

    // === VÍDEO ===
    if (tipo === 'video') {
      if (forceEmbed) {
        if (!embedUrl) return renderFallbackCard(
          'Preview indisponível',
          'Não há URL de embed do Drive para este vídeo.'
        );
        return (
          <iframe
            src={embedUrl}
            title={anexo.nome_original}
            allow="autoplay; encrypted-media; fullscreen"
            allowFullScreen
          />
        );
      }
      if (loading) return renderLoading('Preparando vídeo...');
      if (blobUrl) {
        return (
          <video
            key={blobUrl}
            src={blobUrl}
            controls
            autoPlay
            playsInline
            preload="metadata"
            controlsList="nodownload"
          />
        );
      }
      if (error) {
        return renderFallbackCard(
          'Falha ao carregar vídeo inline',
          error,
          <FaExclamationTriangle />
        );
      }
      return null;
    }

    // === ÁUDIO ===
    if (tipo === 'audio') {
      if (forceEmbed && embedUrl) {
        return <iframe src={embedUrl} title={anexo.nome_original} />;
      }
      if (loading) return renderLoading('Preparando áudio...');
      if (blobUrl) {
        return (
          <div className="preview-audio-wrapper">
            <div className="preview-audio-icon">{tipoIcon('audio')}</div>
            <audio key={blobUrl} src={blobUrl} controls autoPlay />
            <div className="preview-audio-name">{anexo.nome_original}</div>
          </div>
        );
      }
      if (error) return renderFallbackCard('Falha ao carregar áudio', error);
      return null;
    }

    // === IMAGEM ===
    if (tipo === 'imagem') {
      if (forceEmbed && embedUrl) {
        return <iframe src={embedUrl} title={anexo.nome_original} />;
      }
      if (loading) return renderLoading('Carregando imagem...');
      if (blobUrl) return <img src={blobUrl} alt={anexo.nome_original} />;
      if (error) return renderFallbackCard('Falha ao carregar imagem', error);
      return null;
    }

    // === PDF ===
    if (tipo === 'pdf') {
      if (embedUrl) {
        return (
          <iframe
            src={embedUrl}
            title={anexo.nome_original}
            allow="fullscreen"
            allowFullScreen
          />
        );
      }
      return renderFallbackCard(
        'PDF sem preview',
        'Não foi possível gerar um preview via Drive. Abra no Drive ou baixe.'
      );
    }

    // === OFFICE / OUTROS ===
    // Embed do Drive costuma renderizar Word/Excel/PPT
    if (['word', 'excel', 'ppt'].includes(tipo) && embedUrl) {
      return (
        <iframe
          src={embedUrl}
          title={anexo.nome_original}
          allow="fullscreen"
          allowFullScreen
        />
      );
    }

    return renderFallbackCard(
      'Pré-visualização não disponível',
      'Este tipo de arquivo não tem preview inline. Você pode baixá-lo ou abrir no Google Drive.'
    );
  };

  if (!anexo) return null;

  return (
    <div className="bc-modal-overlay" onClick={handleClose}>
      <div
        className="bc-modal-content anexo-preview-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Pré-visualização: ${anexo.nome_original}`}
      >
        <div className="anexo-preview-header">
          <div className="anexo-preview-header-info">
            <h3 title={anexo.nome_original}>{anexo.nome_original}</h3>
            <p>
              {anexo.mimetype || 'arquivo'} • {formatBytes(tamanho)}
              {tooLargeForBlob && (
                <span className="anexo-large-hint">
                  {' '}• Arquivo grande — usando player do Drive
                </span>
              )}
            </p>
          </div>
          <div className="anexo-preview-actions">
            {externalUrl && (
              <a
                href={externalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="preview-action-btn"
                title="Abrir no Google Drive"
              >
                <FaExternalLinkAlt /> Drive
              </a>
            )}
            <button
              className="preview-action-btn primary"
              onClick={() => onDownload && onDownload(anexo.id, anexo.nome_original)}
              title="Baixar anexo"
            >
              <FaDownload /> Baixar
            </button>
            <button
              className="preview-action-btn preview-action-close"
              onClick={handleClose}
              aria-label="Fechar"
              title="Fechar"
            >
              <FaTimes />
            </button>
          </div>
        </div>

        <div className="anexo-preview-body single">
          <div className="anexo-preview-stage">
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
}